import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormEvacZoneStatus } from 'shared/types';
import { getEvacZoneDefaultDescription } from 'shared/utils';

type EvacStatusKey =
  | 'evacuationOrders'
  | 'evacuationWarnings'
  | 'evacuationAdvisories'
  | 'evacuationShelterInPlace';

type CustomEvacState = Record<EvacStatusKey, string | undefined>;

type OnCheckboxCheckProps = {
  enabled: boolean;
  checkboxName: string;
  fieldName: EvacStatusKey;
  fieldEvacZones: FormEvacZoneStatus[];
};

type OnCheckboxCheck = (props: OnCheckboxCheckProps) => void;

type UseEvacZoneSyncResult = {
  customEvacValues: CustomEvacState;
  onCheckboxChange: OnCheckboxCheck;
};

type UseEvacZoneSyncProps = {
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
  customShelterInPlace: boolean;
};

export const useEvacZoneSync = (
  props: UseEvacZoneSyncProps,
): UseEvacZoneSyncResult => {
  const {
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
  } = props;

  const { formState, getValues, setValue, clearErrors } = useFormContext();

  // Keeping our own copy of state since the rich text editor for these is
  // uncontrolled. This allows us to restore the "custom" evacuation values when
  // toggling the checkbox that enables/disables the uncontrolled text fields, but
  // we'll only set it if it's been explicitly given (as indicated by the checkbox
  // already being checked)
  const [customEvacValues, setCustomEvacValues] = useState<CustomEvacState>({
    evacuationOrders: customOrders
      ? formState.defaultValues?.evacuationOrders
      : undefined,
    evacuationWarnings: customWarnings
      ? formState.defaultValues?.evacuationWarnings
      : undefined,
    evacuationAdvisories: customAdvisories
      ? formState.defaultValues?.evacuationAdvisories
      : undefined,
    evacuationShelterInPlace: customShelterInPlace
      ? formState.defaultValues?.evacuationShelterInPlace
      : undefined,
  });

  const onCheckboxChange = useCallback(
    (onCheckboxCheckProps: OnCheckboxCheckProps) => {
      const { enabled, checkboxName, fieldName, fieldEvacZones } =
        onCheckboxCheckProps;

      setValue(checkboxName, enabled, { shouldDirty: true });
      clearErrors(fieldName);
      if (!enabled) {
        // capturing the current value of the field to restore if the checkbox is reenabled
        setCustomEvacValues((current) => ({
          ...current,
          [fieldName]: getValues(fieldName),
        }));
        setValue(fieldName, '', { shouldDirty: true });
      } else {
        // if enabling, restore the fields value to first available value in order:
        const fieldValue =
          customEvacValues?.[fieldName] || // the last value entered into the field (from customEvacValues)
          getEvacZoneDefaultDescription(fieldEvacZones) || // default value based on the evac zones (via getEvacZoneDefaultDescription)
          formState.defaultValues?.[fieldName]; // the value initially set in the form (via defaultValues)

        if (fieldValue && customEvacValues?.[fieldName] !== fieldValue) {
          // If we get a new value (which likely came from getEvacZoneDefaultDescription), update
          // customEvacValues so the form and state are in sync
          setCustomEvacValues((current) => ({
            ...current,
            [fieldName]: fieldValue,
          }));
        }
        setValue(fieldName, fieldValue, { shouldDirty: true });
      }
    },
    [
      setValue,
      clearErrors,
      formState.defaultValues,
      getValues,
      customEvacValues,
    ],
  );

  return { customEvacValues, onCheckboxChange };
};
